import { Preferences } from '@capacitor/preferences'

/**
 * Wrapper for Capacitor Preferences plugin
 * Provides type-safe key-value storage
 */

export async function set(key: string, value: string): Promise<void> {
    await Preferences.set({ key, value })
}

export async function get(key: string): Promise<string | null> {
    const { value } = await Preferences.get({ key })
    return value
}

export async function remove(key: string): Promise<void> {
    await Preferences.remove({ key })
}

export async function clear(): Promise<void> {
    await Preferences.clear()
}

export async function keys(): Promise<string[]> {
    const { keys } = await Preferences.keys()
    return keys
}

/**
 * Set a JSON object as a preference
 */
export async function setObject<T>(key: string, value: T): Promise<void> {
    await set(key, JSON.stringify(value))
}

/**
 * Get a JSON object from preferences
 */
export async function getObject<T>(key: string): Promise<T | null> {
    const value = await get(key)
    if (!value) return null

    try {
        return JSON.parse(value) as T
    } catch {
        return null
    }
}

/**
 * Clear all user-related storage data while preserving admin settings
 * This is used when "forgetting" a user to ensure all their data is removed
 *
 * @param workerId - Optional worker ID to clear worker-specific keys
 */
export async function clearUserData(workerId?: number): Promise<void> {
    const allKeys = await keys()

    // Admin-related keys that should be preserved
    const adminKeys = [
        'admin_password_hash',
        'admin_password_salt',
        'admin_god_password_hash',
        'admin_god_password_salt',
        'admin_session',
        'admin_failed_attempts',
        'admin_logs',
        'security_logs'
    ]

    // Keys to clear
    const userKeys = [
        'auth_token',
        'user_profile',
        'app_config',
        'user_locale',
        'ble_attendance_enabled',
        'notification_settings',
        'warden-theme',
        'device_push_token',
        'totp_secret',
        'totp_secret_synced_at',
        'debug_settings'
    ]

    // Clear worker-specific keys
    if (workerId) {
        userKeys.push(`permissions_requested_${workerId}`)
    }

    // Clear all user keys
    for (const key of userKeys) {
        await remove(key)
    }

    // Clear dynamic keys (attendance status entries, token registrations, etc.)
    for (const key of allKeys) {
        // Skip admin keys
        if (adminKeys.includes(key)) {
            continue
        }

        // Clear attendance status keys (attendance_status_*)
        if (key.startsWith('attendance_status_')) {
            await remove(key)
        }

        // Clear any permissions_requested keys
        if (key.startsWith('permissions_requested_')) {
            await remove(key)
        }

        // Clear token registration keys (token_registered_for_worker_*)
        if (key.startsWith('token_registered_for_worker_')) {
            await remove(key)
        }
    }
}
